import { useEffect, useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { Button } from "@/components/ui/button";
import {
  ChevronLeft,
  ChevronRight,
  FileText,
  LinkIcon,
  TrendingUp,
  ExternalLink,
  BarChart4,
  LineChart,
  Info,
  Calendar,
  Globe,
  Share2,
  Loader2,
} from "lucide-react";
import { Card } from "@/components/ui/card";
import { Separator } from "@/components/ui/separator";
import { Badge } from "@/components/ui/badge";
import { useQuery } from "@tanstack/react-query";
import { ApiService } from "@/services/api-service";
import type { SuggestedTopic } from "@/types/types";
import CheckCircle from "@/assets/icons/check_circle.svg";
import { MarkdownToJSX } from "@/components/markdowntojsx";
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from "@/components/ui/tooltip";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";

import {
  Area,
  AreaChart,
  Bar,
  BarChart,
  CartesianGrid,
  Legend,
  Line,
  LineChart as RechartsLineChart,
  ResponsiveContainer,
  Tooltip as RechartsTooltip,
  XAxis,
  YAxis,
} from "recharts";
import { createFileRoute, Link } from "@tanstack/react-router";

export const Route = createFileRoute("/app/_layout/editors-brief")({
  component: EditorsBrief,
});

function EditorsBrief() {
  const [currentTopicPage, setCurrentTopicPage] = useState(0);
  const [topicsPerPage, setTopicsPerpage] = useState(4);
  const [activeTopic, setActiveTopic] = useState<SuggestedTopic | null>(null);
  const [activeTab, setActiveTab] = useState("blueprint"); // blueprint, links, trends
  const [chartType, setChartType] = useState("area"); // area, line, bar
  const [timeRange, setTimeRange] = useState("6m"); // 1m, 3m, 6m, 1y
  const [isMobile, setIsMobile] = useState(false);

  useEffect(() => {
    const checkIfMobile = () => {
      const mobile = window.innerWidth < 768;
      setIsMobile(mobile);
      if (mobile) setTopicsPerpage(3);
    };

    checkIfMobile();

    window.addEventListener("resize", checkIfMobile);

    return () => window.removeEventListener("resize", checkIfMobile);
  }, []);

  const { data: writingExists } = useQuery({
    queryKey: ["has-writing-pattern"],
    queryFn: ApiService.hasWritingPatterns,
  });

  const { data: suggestedTopics, isLoading } = useQuery({
    queryKey: ["suggested-topics"],
    queryFn: ApiService.getsuggestedTopics,
  });

  useEffect(() => {
    if (suggestedTopics && suggestedTopics.length > 0) {
      setActiveTopic(suggestedTopics[0]);
    }
  }, [suggestedTopics]);

  const { data: trendData, isLoading: isTrendLoading } = useQuery({
    queryKey: ["topic-trends", activeTopic?.topic, timeRange],
    queryFn: () => {
      // This would be replaced with actual API call
      if (timeRange === "1m") {
        return [
          { date: "Week 1", value: 22, engagement: 15, shares: 8 },
          { date: "Week 2", value: 25, engagement: 18, shares: 10 },
          { date: "Week 3", value: 28, engagement: 22, shares: 12 },
          { date: "Week 4", value: 32, engagement: 25, shares: 15 },
        ];
      } else if (timeRange === "3m") {
        return [
          { date: "Jan", value: 18, engagement: 12, shares: 5 },
          { date: "Feb", value: 22, engagement: 15, shares: 8 },
          { date: "Mar", value: 28, engagement: 22, shares: 12 },
        ];
      } else if (timeRange === "1y") {
        return [
          { date: "Jan", value: 10, engagement: 5, shares: 2 },
          { date: "Feb", value: 15, engagement: 8, shares: 4 },
          { date: "Mar", value: 25, engagement: 15, shares: 8 },
          { date: "Apr", value: 22, engagement: 12, shares: 6 },
          { date: "May", value: 30, engagement: 18, shares: 10 },
          { date: "Jun", value: 35, engagement: 22, shares: 12 },
          { date: "Jul", value: 40, engagement: 25, shares: 15 },
          { date: "Aug", value: 38, engagement: 24, shares: 14 },
          { date: "Sep", value: 45, engagement: 30, shares: 18 },
          { date: "Oct", value: 50, engagement: 35, shares: 22 },
          { date: "Nov", value: 55, engagement: 40, shares: 25 },
          { date: "Dec", value: 60, engagement: 45, shares: 30 },
        ];
      } else {
        // Default 6m
        return [
          { date: "Jan", value: 15, engagement: 8, shares: 4 },
          { date: "Feb", value: 22, engagement: 15, shares: 8 },
          { date: "Mar", value: 28, engagement: 22, shares: 12 },
          { date: "Apr", value: 35, engagement: 25, shares: 15 },
          { date: "May", value: 42, engagement: 30, shares: 18 },
          { date: "Jun", value: 50, engagement: 38, shares: 25 },
        ];
      }
    },
    enabled: !!activeTopic && activeTab === "trends",
  });

  // Geographic distribution data
  const { data: geoData, isLoading: isGeoLoading } = useQuery({
    queryKey: ["topic-geo", activeTopic?.topic],
    queryFn: () => {
      // This would be replaced with actual API call
      return [
        { region: "North America", value: 45 },
        { region: "Europe", value: 30 },
        { region: "Asia", value: 15 },
        { region: "Other", value: 10 },
      ];
    },
    enabled: !!activeTopic && activeTab === "trends",
  });

  // Related topics with correlation scores
  const { data: relatedTopics, isLoading: isRelatedLoading } = useQuery({
    queryKey: ["related-topics", activeTopic?.topic],
    queryFn: () => {
      // This would be replaced with actual API call
      return [
        { topic: "AI Writing", correlation: 0.85 },
        { topic: "Content Strategy", correlation: 0.78 },
        { topic: "Editorial Planning", correlation: 0.72 },
        { topic: "SEO", correlation: 0.65 },
        { topic: "Social Media", correlation: 0.6 },
      ];
    },
    enabled: !!activeTopic && activeTab === "trends",
  });

  const formatTopicTitle = (topic: string) => {
    return topic.replace(/^#\s+/, "");
  };

  const renderLinkDomain = (url: string) => {
    try {
      const domain = new URL(url).hostname.replace("www.", "");
      return domain;
    } catch (e) {
      console.log(e);
      return url;
    }
  };

  const calculateGrowth = () => {
    if (!trendData || trendData.length < 2) return 0;

    const firstValue = trendData[0].value;
    const lastValue = trendData[trendData.length - 1].value;

    return Math.round(((lastValue - firstValue) / firstValue) * 100);
  };

  const getGrowthColor = (growth: number) => {
    if (growth > 20) return "text-green-600 dark:text-green-400";
    if (growth > 0) return "text-green-500 dark:text-green-300";
    if (growth === 0) return "text-gray-500 dark:text-gray-400";
    if (growth > -20) return "text-orange-500 dark:text-orange-300";
    return "text-red-500 dark:text-red-300";
  };

  const getGrowthIcon = (growth: number) => {
    if (growth > 0) return <TrendingUp className="h-3 w-3 mr-1" />;
    if (growth === 0) return null;
    return <TrendingUp className="h-3 w-3 mr-1 rotate-180" />;
  };

  const getChartColors = () => {
    return {
      value: "#8884d8",
      engagement: "#82ca9d",
      shares: "#ffc658",
    };
  };

  const renderChart = () => {
    if (!trendData || trendData.length === 0) return null;

    const colors = getChartColors();

    if (chartType === "area") {
      return (
        <ResponsiveContainer width="100%" height={200}>
          <AreaChart
            data={trendData}
            margin={{ top: 10, right: 10, left: 0, bottom: 0 }}
          >
            <defs>
              <linearGradient id="colorValue" x1="0" y1="0" x2="0" y2="1">
                <stop offset="5%" stopColor={colors.value} stopOpacity={0.8} />
                <stop offset="95%" stopColor={colors.value} stopOpacity={0.1} />
              </linearGradient>
            </defs>
            <XAxis dataKey="date" tick={{ fontSize: 10 }} />
            <YAxis tick={{ fontSize: 10 }} />
            <CartesianGrid strokeDasharray="3 3" opacity={0.1} />
            <RechartsTooltip
              contentStyle={{
                backgroundColor: "rgba(255, 255, 255, 0.9)",
                borderRadius: "8px",
                boxShadow: "0 4px 12px rgba(0, 0, 0, 0.1)",
                border: "none",
                fontSize: "12px",
              }}
            />
            <Area
              type="monotone"
              dataKey="value"
              stroke={colors.value}
              fillOpacity={1}
              fill="url(#colorValue)"
              name="Interest"
            />
          </AreaChart>
        </ResponsiveContainer>
      );
    }

    if (chartType === "line") {
      return (
        <ResponsiveContainer width="100%" height={200}>
          <RechartsLineChart
            data={trendData}
            margin={{ top: 10, right: 10, left: 0, bottom: 0 }}
          >
            <XAxis dataKey="date" tick={{ fontSize: 10 }} />
            <YAxis tick={{ fontSize: 10 }} />
            <CartesianGrid strokeDasharray="3 3" opacity={0.1} />
            <RechartsTooltip
              contentStyle={{
                backgroundColor: "rgba(255, 255, 255, 0.9)",
                borderRadius: "8px",
                boxShadow: "0 4px 12px rgba(0, 0, 0, 0.1)",
                border: "none",
                fontSize: "12px",
              }}
            />
            <Legend
              verticalAlign="top"
              height={36}
              iconSize={10}
              iconType="circle"
            />
            <Line
              type="monotone"
              dataKey="value"
              stroke={colors.value}
              strokeWidth={2}
              dot={{ r: 3 }}
              activeDot={{ r: 5 }}
              name="Interest"
            />
            <Line
              type="monotone"
              dataKey="engagement"
              stroke={colors.engagement}
              strokeWidth={2}
              dot={{ r: 3 }}
              activeDot={{ r: 5 }}
              name="Engagement"
            />
            <Line
              type="monotone"
              dataKey="shares"
              stroke={colors.shares}
              strokeWidth={2}
              dot={{ r: 3 }}
              activeDot={{ r: 5 }}
              name="Shares"
            />
          </RechartsLineChart>
        </ResponsiveContainer>
      );
    }

    return (
      <ResponsiveContainer width="100%" height={200}>
        <BarChart
          data={trendData}
          margin={{ top: 10, right: 10, left: 0, bottom: 0 }}
        >
          <XAxis dataKey="date" tick={{ fontSize: 10 }} />
          <YAxis tick={{ fontSize: 10 }} />
          <CartesianGrid strokeDasharray="3 3" opacity={0.1} />
          <RechartsTooltip
            contentStyle={{
              backgroundColor: "rgba(255, 255, 255, 0.9)",
              borderRadius: "8px",
              boxShadow: "0 4px 12px rgba(0, 0, 0, 0.1)",
              border: "none",
              fontSize: "12px",
            }}
          />
          <Legend
            verticalAlign="top"
            height={36}
            iconSize={10}
            iconType="circle"
          />
          <Bar
            dataKey="value"
            fill={colors.value}
            name="Interest"
            radius={[4, 4, 0, 0]}
          />
          <Bar
            dataKey="engagement"
            fill={colors.engagement}
            name="Engagement"
            radius={[4, 4, 0, 0]}
          />
          <Bar
            dataKey="shares"
            fill={colors.shares}
            name="Shares"
            radius={[4, 4, 0, 0]}
          />
        </BarChart>
      </ResponsiveContainer>
    );
  };

  const renderGeoDistribution = () => {
    if (!geoData || isGeoLoading) return null;

    const COLORS = ["#8884d8", "#83a6ed", "#8dd1e1", "#82ca9d"];

    return (
      <div className="mt-6 space-y-2">
        <h5 className="text-xs font-medium flex items-center">
          <Globe className="h-3 w-3 mr-1" />
          Geographic Distribution
        </h5>
        <div className="flex items-center justify-between">
          {geoData.map((entry, index) => (
            <div key={index} className="flex flex-col items-center">
              <div className="w-full flex items-center justify-center">
                <div
                  className="rounded-full"
                  style={{
                    backgroundColor: COLORS[index % COLORS.length],
                    width: `${Math.max(entry.value / 2, 20)}px`,
                    height: `${Math.max(entry.value / 2, 20)}px`,
                  }}
                />
              </div>
              <div className="text-xs mt-1">{entry.region}</div>
              <div className="text-xs font-medium">{entry.value}%</div>
            </div>
          ))}
        </div>
      </div>
    );
  };

  const renderRelatedTopics = () => {
    if (!relatedTopics || isRelatedLoading) return null;

    return (
      <div className="mt-6">
        <h5 className="text-xs font-medium mb-2 flex items-center">
          <Share2 className="h-3 w-3 mr-1" />
          Related Trending Topics
        </h5>
        <div className="flex flex-wrap gap-2">
          {relatedTopics.map((topic, idx) => (
            <TooltipProvider key={idx}>
              <Tooltip>
                <TooltipTrigger asChild>
                  <Badge
                    variant="secondary"
                    className="cursor-pointer"
                    style={{
                      opacity: 0.5 + topic.correlation / 2, // Visual representation of correlation
                    }}
                  >
                    {topic.topic}
                  </Badge>
                </TooltipTrigger>
                <TooltipContent>
                  <p className="text-xs">
                    {Math.round(topic.correlation * 100)}% correlation
                  </p>
                </TooltipContent>
              </Tooltip>
            </TooltipProvider>
          ))}
        </div>
      </div>
    );
  };

  return (
    <motion.div
      initial={{ opacity: 0, y: 50, x: 0 }}
      animate={{ opacity: 1, y: 0, x: 0 }}
      exit={{ opacity: 0, y: 0, x: 0 }}
      transition={{ duration: 0.3 }}
    >
      <div className="bg-white dark:bg-neutral-950 py-6 rounded-3xl w-full max-w-4xl mx-auto shadow-2xl dark:shadow-md dark:shadow-neutral-900">
        {/* Header */}
        <div className="flex justify-between items-center px-6 pb-4">
          <h2 className="text-base font-bold tracking-tight">EDITOR'S BRIEF</h2>
        </div>
        <Separator />

        {/* Topics Section */}
        <div className="space-y-4 p-6 ">
          {isLoading ? (
            <div className="w-full col-span-4 flex-1 flex justify-center mx-auto items-center h-32">
              <Loader2 className="animate-spin h-8 w-8" />
            </div>
          ) : suggestedTopics?.length ? (
            <>
              <div className="flex justify-between items-center sticky">
                <h3 className="text-sm font-bold tracking-tight">
                  RELATED SUBJECT & ANGLES
                </h3>
                <div className="flex space-x-2">
                  <Button
                    type="button"
                    variant="ghost"
                    size="icon"
                    className="h-6 w-6"
                    onClick={() => {
                      if (currentTopicPage > 0) {
                        setCurrentTopicPage((prev) => prev - 1);
                      }
                    }}
                    disabled={currentTopicPage === 0 || isLoading}
                  >
                    <ChevronLeft className="h-4 w-4" />
                  </Button>
                  <Button
                    type="button"
                    variant="ghost"
                    size="icon"
                    className="h-6 w-6"
                    onClick={() => {
                      if (
                        suggestedTopics &&
                        (currentTopicPage + 1) * topicsPerPage <
                          suggestedTopics.length
                      ) {
                        setCurrentTopicPage((prev) => prev + 1);
                      }
                    }}
                    disabled={
                      !suggestedTopics ||
                      (currentTopicPage + 1) * topicsPerPage >=
                        suggestedTopics.length ||
                      isLoading
                    }
                  >
                    <ChevronRight className="h-4 w-4" />
                  </Button>
                </div>
              </div>

              <div
                className={`grid ${
                  isMobile ? "grid-cols-3" : "grid-cols-4"
                } gap-4 w-full`}
              >
                {suggestedTopics
                  .slice(
                    currentTopicPage * topicsPerPage,
                    (currentTopicPage + 1) * topicsPerPage
                  )
                  .map((topic, index) => (
                    <motion.div
                      key={index}
                      initial={{ opacity: 0, y: 10 }}
                      animate={{ opacity: 1, y: 0 }}
                      exit={{ opacity: 0, y: -10 }}
                      transition={{
                        duration: 0.3,
                        delay: index * 0.05,
                      }}
                    >
                      <Card
                        className={`p-2 sm:p-4 h-[120px] sm:h-[140px] cursor-pointer transition-colors relative ${
                          activeTopic === topic
                            ? "bg-secondary-50 hover:bg-secondary-100 dark:bg-secondary-200 dark:hover:bg-secondary-300"
                            : "bg-neutral-50 hover:bg-neutral-100 dark:bg-neutral-950 dark:hover:bg-neutral-900"
                        }`}
                        onClick={() => {
                          const thisTopic =
                            activeTopic === topic ? null : topic;
                          setActiveTopic(thisTopic);
                          setActiveTab("blueprint");
                        }}
                      >
                        {activeTopic === topic ? (
                          <img
                            src={CheckCircle || "/placeholder.svg"}
                            alt="check circle"
                            className="w-3 sm:w-5 h-3 sm:h-5 absolute right-2 sm:right-3 top-2 sm:top-3"
                          />
                        ) : null}
                        <div
                          className={`flex flex-col items-center space-y-2 ${
                            activeTopic === topic
                              ? "text-secondary-900 dark:text-secondary-900"
                              : "text-neutral-900 dark:text-neutral-100"
                          }`}
                        >
                          <div className="p-2 rounded-md">
                            <FileText className="h-5 w-5" />
                          </div>
                          <p className="text-[10px] sm:text-xs text-center line-clamp-3">
                            {formatTopicTitle(topic.topic)}
                          </p>
                        </div>
                      </Card>
                    </motion.div>
                  ))}
              </div>
            </>
          ) : writingExists ? (
            <div className="col-span-5 flex justify-center items-center text-sm text-muted-foreground h-32 text-center">
              <p className="text-center sm:w-2xl">
                We're preparing your personalized Editor's Brief. It's on the
                way and will be available shortly. In the meantime, feel free to
                explore Ghostwriting and the Inkriv Assistant.
              </p>
            </div>
          ) : (
            <div className="col-span-5 flex justify-center items-center text-sm text-muted-foreground h-32 text-center">
              <p className="text-center sm:w-2xl">
                You haven't personalized your writing style yet.{" "}
                <Link
                  to={"/app/personalization"}
                  className="underline text-blue-600"
                >
                  Upload past writings
                </Link>{" "}
                to unlock your tailored Editor's Brief and get the most out of
                Inkriv.
              </p>
            </div>
          )}
        </div>

        <AnimatePresence>
          {activeTopic && (
            <motion.div
              initial={{ opacity: 0, height: 0 }}
              animate={{ opacity: 1, height: "auto" }}
              exit={{ opacity: 0, height: 0 }}
              transition={{ duration: 0.3 }}
            >
              <Separator />

              {/* Tab Navigation */}
              <div className="flex border-b px-6 py-4 ">
                <Button
                  variant={activeTab === "blueprint" ? "secondary" : "ghost"}
                  className="text-xs rounded-b-none border-b-2 border-transparent"
                  onClick={() => setActiveTab("blueprint")}
                >
                  Blueprint
                </Button>
                <Button
                  variant={activeTab === "links" ? "secondary" : "ghost"}
                  className="text-xs rounded-b-none border-b-2 border-transparent"
                  onClick={() => setActiveTab("links")}
                >
                  <LinkIcon className="h-3 w-3 mr-1" />
                  Sources (
                  {Object.entries(activeTopic.open_source_links).map(
                    ([url]) => url
                  ).length || 0}
                  )
                </Button>
                {/* <Button
                  variant={activeTab === "trends" ? "secondary" : "ghost"}
                  className="text-xs rounded-b-none border-b-2 border-transparent"
                  onClick={() => setActiveTab("trends")}
                >
                  <TrendingUp className="h-3 w-3 mr-1" />
                  Trends
                </Button> */}
              </div>

              {/* Content Area */}
              <div className="p-6">
                {activeTab === "blueprint" && (
                  <div className="prose prose-sm dark:prose-invert">
                    <MarkdownToJSX text={activeTopic.blueprint} />
                  </div>
                )}

                {activeTab === "links" && (
                  <div className="space-y-6 overflow-x-auto">
                    <h4 className="text-sm font-medium">Source Links</h4>

                    {/* Accessible Links */}
                    {Object.entries(activeTopic.open_source_links || {})
                      .length > 0 && (
                      <div>
                        {/* <h5 className="text-xs font-semibold text-green-600 mb-2">
                          Accessible Links
                        </h5> */}
                        <ul className="space-y-3">
                          {Object.entries(activeTopic.open_source_links).map(
                            ([link], idx) => (
                              <li
                                key={idx}
                                className="flex items-start sm:items-center flex-col sm:flex-row"
                              >
                                <Badge
                                  className="mr-2 mt-0.5"
                                  variant="outline"
                                >
                                  {renderLinkDomain(link)}
                                </Badge>

                                {/* Mobile */}
                                <a
                                  href={link}
                                  target="_blank"
                                  rel="noopener noreferrer"
                                  className="sm:hidden text-xs group text-blue-600 dark:text-blue-400 hover:text-blue-800 dark:hover:text-blue-300 flex items-center"
                                >
                                  {link.length > 30
                                    ? link.substring(0, 30) + "..."
                                    : link}
                                  <ExternalLink className="h-3 w-3 ml-1 opacity-50 group-hover:opacity-100" />
                                </a>

                                {/* Desktop */}
                                <a
                                  href={link}
                                  target="_blank"
                                  rel="noopener noreferrer"
                                  className="hidden sm:flex text-xs pt-1 group text-blue-600 dark:text-blue-400 hover:text-blue-800 dark:hover:text-blue-300 items-center"
                                >
                                  {link.length > 50
                                    ? link.substring(0, 50) + "..."
                                    : link}
                                  <ExternalLink className="h-3 w-3 ml-1 opacity-50 group-hover:opacity-100" />
                                </a>
                              </li>
                            )
                          )}
                        </ul>
                      </div>
                    )}

                    {/* Unavailable Links */}
                    {/* {Object.entries(activeTopic.open_source_links || {}).filter(
                      ([, v]) => !v
                    ).length > 0 && (
                      <div>
                        <h5 className="text-xs font-semibold text-red-500 mb-2">
                          Unaccessible Links
                        </h5>
                        <ul className="space-y-2">
                          {Object.entries(activeTopic.open_source_links)
                            .filter(([, isAccessible]) => !isAccessible)
                            .map(([link], idx) => (
                              <li
                                key={idx}
                                className="flex items-start sm:items-center flex-col sm:flex-row text-xs text-muted-foreground italic"
                              >
                                <Badge
                                  className="mr-2 mt-0.5"
                                  variant="outline"
                                >
                                  {renderLinkDomain(link)}
                                </Badge>
                                <a
                                  href={link}
                                  className="underline flex items-center flex-nowrap"
                                  target="_blank"
                                >
                                  {link.length > 50
                                    ? link.substring(0, 50) + "..."
                                    : link}
                                  <ExternalLink className="h-3 w-3 ml-1 opacity-50 group-hover:opacity-100" />
                                </a>
                              </li>
                            ))}
                        </ul>
                      </div>
                    )} */}

                    {/* No links at all */}
                    {Object.keys(activeTopic.open_source_links || {}).length ===
                      0 && (
                      <p className="text-sm text-muted-foreground">
                        No source links available
                      </p>
                    )}
                  </div>
                )}

                {activeTab === "trends" && (
                  <div className="space-y-4">
                    <div className="flex justify-between items-center">
                      <h4 className="text-sm font-medium flex items-center">
                        <TrendingUp className="h-4 w-4 mr-1" />
                        Topic Trends
                        <TooltipProvider>
                          <Tooltip>
                            <TooltipTrigger asChild>
                              <Info className="h-3 w-3 ml-1 text-muted-foreground cursor-help" />
                            </TooltipTrigger>
                            <TooltipContent>
                              <p className="text-xs max-w-xs">
                                Trend data shows interest, engagement, and
                                sharing metrics for this topic over time.
                              </p>
                            </TooltipContent>
                          </Tooltip>
                        </TooltipProvider>
                      </h4>

                      <div className="flex space-x-2">
                        <Select value={timeRange} onValueChange={setTimeRange}>
                          <SelectTrigger className="h-7 text-xs w-20">
                            <Calendar className="h-3 w-3 mr-1" />
                            <SelectValue placeholder="Time" />
                          </SelectTrigger>
                          <SelectContent>
                            <SelectItem value="1m">1 Month</SelectItem>
                            <SelectItem value="3m">3 Months</SelectItem>
                            <SelectItem value="6m">6 Months</SelectItem>
                            <SelectItem value="1y">1 Year</SelectItem>
                          </SelectContent>
                        </Select>

                        <div className="flex border rounded-md overflow-hidden">
                          <Button
                            size="sm"
                            variant={
                              chartType === "area" ? "secondary" : "ghost"
                            }
                            className="h-7 px-2"
                            onClick={() => setChartType("area")}
                          >
                            <AreaChart className="h-3 w-3" />
                          </Button>
                          <Button
                            size="sm"
                            variant={
                              chartType === "line" ? "secondary" : "ghost"
                            }
                            className="h-7 px-2"
                            onClick={() => setChartType("line")}
                          >
                            <LineChart className="h-3 w-3" />
                          </Button>
                          <Button
                            size="sm"
                            variant={
                              chartType === "bar" ? "secondary" : "ghost"
                            }
                            className="h-7 px-2"
                            onClick={() => setChartType("bar")}
                          >
                            <BarChart4 className="h-3 w-3" />
                          </Button>
                        </div>
                      </div>
                    </div>

                    {isTrendLoading ? (
                      <div className="flex justify-center py-8">
                        <Loader2 className="animate-spin h-6 w-6" />
                      </div>
                    ) : trendData && trendData.length > 0 ? (
                      <div className="pt-4">
                        {renderChart()}

                        <div className="flex justify-between mt-4">
                          <Badge
                            variant="outline"
                            className={`${
                              calculateGrowth() > 0
                                ? "bg-green-50 text-green-700 dark:bg-green-900 dark:text-green-100"
                                : calculateGrowth() === 0
                                  ? "bg-gray-50 text-gray-700 dark:bg-gray-900 dark:text-gray-100"
                                  : "bg-red-50 text-red-700 dark:bg-red-900 dark:text-red-100"
                            }`}
                          >
                            {getGrowthIcon(calculateGrowth())}
                            {calculateGrowth() > 0
                              ? "Trending Up"
                              : calculateGrowth() === 0
                                ? "Stable"
                                : "Trending Down"}
                          </Badge>
                          <span
                            className={`text-xs ${getGrowthColor(
                              calculateGrowth()
                            )}`}
                          >
                            {calculateGrowth() > 0 ? "+" : ""}
                            {calculateGrowth()}% growth
                          </span>
                        </div>

                        <div className="grid grid-cols-3 gap-4 mt-4">
                          <div className="bg-neutral-50 dark:bg-neutral-900 p-3 rounded-lg">
                            <div className="text-xs text-muted-foreground">
                              Current Interest
                            </div>
                            <div className="text-lg font-bold">
                              {trendData[trendData.length - 1].value}
                            </div>
                          </div>
                          <div className="bg-neutral-50 dark:bg-neutral-900 p-3 rounded-lg">
                            <div className="text-xs text-muted-foreground">
                              Engagement
                            </div>
                            <div className="text-lg font-bold">
                              {trendData[trendData.length - 1].engagement}
                            </div>
                          </div>
                          <div className="bg-neutral-50 dark:bg-neutral-900 p-3 rounded-lg">
                            <div className="text-xs text-muted-foreground">
                              Shares
                            </div>
                            <div className="text-lg font-bold">
                              {trendData[trendData.length - 1].shares}
                            </div>
                          </div>
                        </div>

                        {renderGeoDistribution()}
                        {renderRelatedTopics()}
                      </div>
                    ) : (
                      <p className="text-sm text-muted-foreground">
                        No trend data available
                      </p>
                    )}
                  </div>
                )}
              </div>
            </motion.div>
          )}
        </AnimatePresence>
      </div>
    </motion.div>
  );
}
